import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person,
  Hotel,
  Receipt,
  History,
  Star,
  CalendarToday,
  AttachMoney,
  RoomService,
  MoreVert,
  Visibility,
  Cancel,
  Download,
  Add,
} from '@mui/icons-material';
import CustomerBookingDialog from '../components/CustomerBookingDialog';
import ServiceOrderDialog from '../components/ServiceOrderDialog';

const initialBookings = [
  {
    id: 'BK2025',
    roomType: 'Deluxe',
    roomNumber: 'DEL-204',
    checkIn: '2025-11-20',
    checkOut: '2025-11-23',
    status: 'Upcoming',
    totalAmount: 540,
    nights: 3,
    guests: 2,
  },
  {
    id: 'BK2024',
    roomType: 'Suite',
    roomNumber: 'SUI-305',
    checkIn: '2025-10-15',
    checkOut: '2025-10-18',
    status: 'Completed',
    totalAmount: 840,
    nights: 3,
    guests: 2,
  },
];

const myServices = [
  { id: 1, service: 'Room Service', date: '2025-10-16', amount: 45, status: 'Completed', items: 'Breakfast, Coffee' },
  { id: 2, service: 'Laundry', date: '2025-10-17', amount: 25, status: 'Completed', items: '5 items' },
  { id: 3, service: 'Spa', date: '2025-10-16', amount: 120, status: 'Completed', items: 'Swedish Massage 60min' },
];

export default function CustomerDashboard({ userRole, userName }) {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(initialBookings);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const handleMenuClick = (event, booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleViewBooking = () => {
    setViewDialogOpen(true);
    setAnchorEl(null);
  };

  const handleCancelBooking = () => {
    if (selectedBooking && selectedBooking.status === 'Upcoming') {
      setBookings(bookings.map(b => 
        b.id === selectedBooking.id ? { ...b, status: 'Cancelled' } : b
      ));
      setSnackbar({
        open: true,
        message: `Booking ${selectedBooking.id} cancelled successfully!`,
        severity: 'success',
      });
    }
    setAnchorEl(null);
  };

  const handleSaveBooking = (bookingData) => {
    setBookings([bookingData, ...bookings]);
    setSnackbar({
      open: true,
      message: `Booking confirmed! Booking ID: ${bookingData.id}`,
      severity: 'success',
    });
  };

  const handleBookRoom = () => {
    setBookingDialogOpen(true);
  };

  const handleOrderService = () => {
    setServiceDialogOpen(true);
  };

  const handleViewInvoices = () => {
    navigate('/customer/invoices');
  };

  const handleEditProfile = () => {
    navigate('/customer/profile');
  };

  const handleServiceOrder = (serviceData) => {
    setSnackbar({
      open: true,
      message: `Service order placed successfully!`,
      severity: 'success',
    });
  };

  const totalSpent = bookings.reduce((sum, b) => sum + b.totalAmount, 0) + 
                     myServices.reduce((sum, s) => sum + s.amount, 0);

  const stats = [
    { label: 'Total Bookings', value: bookings.length, icon: Hotel, color: '#1976d2' },
    { label: 'Loyalty Points', value: '2,450', icon: Star, color: '#ed6c02' },
    { label: 'Total Spent', value: `$${totalSpent.toLocaleString()}`, icon: AttachMoney, color: '#2e7d32' },
    { label: 'Member Since', value: '2023', icon: CalendarToday, color: '#9c27b0' },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Welcome Back, {userName}!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Customer Portal - Manage your bookings and profile
        </Typography>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                      <IconComponent />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                My Bookings
              </Typography>
              <Button 
                variant="contained" 
                size="small"
                startIcon={<Add />}
                onClick={() => setBookingDialogOpen(true)}
              >
                New Booking
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell><strong>Booking ID</strong></TableCell>
                    <TableCell><strong>Room</strong></TableCell>
                    <TableCell><strong>Check-in</strong></TableCell>
                    <TableCell><strong>Check-out</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow key={booking.id} hover>
                      <TableCell><strong>{booking.id}</strong></TableCell>
                      <TableCell>{booking.roomType} - {booking.roomNumber}</TableCell>
                      <TableCell>{new Date(booking.checkIn).toLocaleDateString()}</TableCell>
                      <TableCell>{new Date(booking.checkOut).toLocaleDateString()}</TableCell>
                      <TableCell><strong>${booking.totalAmount}</strong></TableCell>
                      <TableCell>
                        <Chip
                          label={booking.status}
                          size="small"
                          color={
                            booking.status === 'Upcoming' ? 'primary' : 
                            booking.status === 'Completed' ? 'success' : 'error'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => handleMenuClick(e, booking)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Service History
            </Typography>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell><strong>Service</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myServices.map((service, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{service.service}</TableCell>
                      <TableCell>{new Date(service.date).toLocaleDateString()}</TableCell>
                      <TableCell><strong>${service.amount}</strong></TableCell>
                      <TableCell>
                        <Chip label={service.status} size="small" color="success" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Profile Information
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {userName}
              </Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">customer@hotelops.com</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Phone
              </Typography>
              <Typography variant="body1">+1 (555) 123-4567</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Membership Level
              </Typography>
              <Chip label="VIP Member" size="small" color="warning" icon={<Star />} />
            </Box>
            <Button variant="outlined" fullWidth sx={{ mt: 2 }} onClick={handleEditProfile}>
              Edit Profile
            </Button>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Quick Actions
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Button variant="outlined" fullWidth sx={{ mb: 1 }} startIcon={<Hotel />} onClick={handleBookRoom}>
              Book a Room
            </Button>
            <Button variant="outlined" fullWidth sx={{ mb: 1 }} startIcon={<RoomService />} onClick={handleOrderService}>
              Order Service
            </Button>
            <Button variant="outlined" fullWidth startIcon={<Receipt />} onClick={handleViewInvoices}>
              View Invoices
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleViewBooking}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>
          <Download fontSize="small" sx={{ mr: 1 }} />
          Download Receipt
        </MenuItem>
        {selectedBooking?.status === 'Upcoming' && (
          <MenuItem onClick={handleCancelBooking} sx={{ color: 'error.main' }}>
            <Cancel fontSize="small" sx={{ mr: 1 }} />
            Cancel Booking
          </MenuItem>
        )}
      </Menu>

      <CustomerBookingDialog
        open={bookingDialogOpen}
        onClose={() => setBookingDialogOpen(false)}
        onSave={handleSaveBooking}
      />

      <ServiceOrderDialog
        open={serviceDialogOpen}
        onClose={() => setServiceDialogOpen(false)}
        onSave={handleServiceOrder}
      />

      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Booking Details</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedBooking.id}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedBooking.status}
                  size="small"
                  color={
                    selectedBooking.status === 'Upcoming' ? 'primary' : 
                    selectedBooking.status === 'Completed' ? 'success' : 'error'
                  }
                />
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Room Type</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedBooking.roomType}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Room Number</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedBooking.roomNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Check-in</Typography>
                <Typography variant="body1">{new Date(selectedBooking.checkIn).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Check-out</Typography>
                <Typography variant="body1">{new Date(selectedBooking.checkOut).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Nights</Typography>
                <Typography variant="body1">{selectedBooking.nights}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Guests</Typography>
                <Typography variant="body1">{selectedBooking.guests}</Typography>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Total Amount</Typography>
                  <Typography variant="h6" color="primary">${selectedBooking.totalAmount}</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
